import { sendPasswordResetEmail, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { ChevronLeft, Eye, EyeClosed, Lock, LucideSquareArrowRightExit, Mail, SendHorizonal, User2 } from 'lucide-react';
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { useAlertContext } from '../contexts/Alert';
import { v4 } from 'uuid';
import { USER_URL } from '../api_base.js';
import { buildHeaders, buildDeviceInfoHeader } from '../utils/apiHeaders.js';
import { checkMaintenance } from '../utils/checkMaintenance.js';

const Auth = () => {
    const [currentPage, setCurrentPage] = useState("sign in");
    const [emailSent, setEmailSent] = useState(false);
    const [loadingButtons, setLoadingButtons] = useState([]);
    const [showPassword, setShowPassword] = useState(false);

    const { setAlerts } = useAlertContext();
    const navigate = useNavigate();

    const pushAlert = (text) => {
        setAlerts(prev => ([
            ...prev,
            { id: v4(), text }
        ]));
    }

    const sendEmail = async (e) => {
        e.preventDefault();
        setLoadingButtons(prev => ([...prev, "reset password"]));
        try {
            await sendPasswordResetEmail(auth, e.target["email"].value.trim(), {
                url: window.location.origin + "/auth",
            });
            setEmailSent(true);
        } catch (err) {
            pushAlert(err.code === "auth/too-many-requests" ? "You have sent many reset password emails, so try again later." : "Oops! something went wrong while sending the email.");
        }
        setLoadingButtons(prev => ([...prev.filter(b => b !== "reset password")]));
    }

    const handleSubmitAuth = async (e) => {
        e.preventDefault();
        const { username, email, password } = e.target;
        const userData = {
            email: email.value.trim(),
            password: password.value
        }
        setLoadingButtons(prev => ([...prev, "submit"]));
        if (currentPage === "sign up") {
            userData.username = username.value.trim();
        }

        const fetchApiAuth = async () => {
            try {
                const res = await fetch(USER_URL + "/" + (currentPage === "sign in" ? "login" : "register"), {
                    method: "POST",
                    credentials: "include",
                    headers: buildHeaders({
                        "Content-Type": "application/json",
                        "Device-Info": buildDeviceInfoHeader(),
                    }),
                    body: JSON.stringify({ userData })
                });
                const data = await res.json();

                if (!res.ok || data.failed) {
                    pushAlert(data.message || `Oops! something went wrong while ${currentPage === "sign in" ? "signing in" : "signing up"}.`);
                    checkMaintenance(data , navigate);
                    return;
                }

                if (data.token) {
                    localStorage.setItem("token", data.token);
                }

                navigate("/");
            } catch (err) {
                console.log(err);
                pushAlert("Oops! something went wrong while reaching the server.");
            } finally {
                setLoadingButtons(prev => ([...prev.filter(b => b !== "submit")]));
            }
        }

        try {
            if (currentPage === "sign in") {
                await signInWithEmailAndPassword(auth, userData.email, userData.password);
            } else {
                await createUserWithEmailAndPassword(auth, userData.email, userData.password);
            }
            await fetchApiAuth();
        } catch (err) {
            setLoadingButtons(prev => ([...prev.filter(b => b !== "submit")]));
            pushAlert(`Oops! something went wrong while ${currentPage === "sign in" ? "signing in" : "signing up"}.`);
        }
    }

    return (
        <div className="auth">
            <div className="left_side">
                <p>Manage all your SME accounts from one place.</p>
            </div>
            <div className="right_side">
                <div className="content">
                    {
                        currentPage === "reset password" ? <>
                            <div className="header">
                                <ChevronLeft onClick={() => setCurrentPage("sign in")} />
                                <p>Reset Password</p>
                            </div>
                            {
                                emailSent ? <div className="email_sent">
                                    <h3>Email Has Been Sent.</h3>
                                    <p>Your reset password email has been sent successfully. The email may be marked as <span>spam</span></p>
                                </div> : <div className="send_email">
                                    <p>Enter your email address to send a reset password link to it.</p>
                                    <form className="field_container" onSubmit={sendEmail}>
                                        <label htmlFor="email">Email Address</label>
                                        <div className="input">
                                            <input name="email" type="email" id="email" placeholder="example@gmail.com" required />
                                            <div className="input_icon"><Mail size="20" /></div>
                                        </div>
                                        <button className="send_email">{loadingButtons.includes("reset password") ? <span className="loading"></span> : <>Send Email <SendHorizonal /></>}</button>
                                    </form>
                                </div>
                            }
                        </> :
                            <>
                                <div className="texts">
                                    <h2>{currentPage === "sign in" ? "Sign Into Your" : "Create Your New"} Account</h2>
                                    {currentPage === "sign in" ?
                                        <p>Welcome back! Sign in to access your account and get started.</p> :
                                        <p>Join us today! Sign up to unlock your personalized dashboard.</p>
                                    }
                                </div>
                                <form onSubmit={handleSubmitAuth}>
                                    {currentPage === "sign up" ? <div className="field_container">
                                        <label htmlFor="username">Username</label>
                                        <div className="input">
                                            <input type="text" minLength={6} maxLength={15} id="username" placeholder="Username" required />
                                            <span className="input_icon">
                                                <User2 size="20" />
                                            </span>
                                        </div>
                                    </div> : undefined}
                                    <div className="field_container">
                                        <label htmlFor="email">Email Address</label>
                                        <div className="input">
                                            <input type="email" id="email" placeholder="example@gmail.com" required />
                                            <span className="input_icon">
                                                <Mail size="20" />
                                            </span>
                                        </div>
                                    </div>
                                    <div className="field_container">
                                        <label htmlFor="password">Password</label>
                                        <div className="input">
                                            <input type={!showPassword ? "password" : "text"} id="password" placeholder="~~~~~~~~" minLength={8} required />
                                            <span className="input_icon">
                                                <Lock size="20" />
                                            </span>
                                        </div>
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="hide_password">{!showPassword ? <EyeClosed size="20" /> : <Eye size="20" />}</button>
                                    </div>
                                    <button className="submit" type="submit">{loadingButtons.includes("submit") ? <span className="loading"></span> : <>{currentPage === "sign in" ? "SIGN IN" : "CREATE ACCOUNT"} <LucideSquareArrowRightExit /></>}</button>
                                </form>
                                <div className="problems">
                                    <div className="account_problem">{currentPage === "sign in" ? "Don't h" : "H"}ave an account? <button onClick={() => setCurrentPage(prev => prev === "sign in" ? "sign up" : "sign in")}>{currentPage === "sign in" ? "Create Account" : "Sign In"}</button></div>
                                    {currentPage === "sign in" ? <div className="password_problem">
                                        <button onClick={() => { setCurrentPage("reset password"); setEmailSent(false); }}>Forget Password?</button>
                                    </div> : undefined}
                                </div>
                            </>
                    }
                </div>
            </div>
        </div>
    )
}

export default Auth;
